/**
 * Import Phone Validation Mixin
 * Handles phone number validation for contact imports
 */
export default {
    data() {
        return {
            // Phone validation state
            validating_phone_numbers: false,
            phone_validation_batch_id: null,
            phone_validation_status: '',
            phone_validation_polling_interval: null,
            used_phone_validation: false,
        };
    },

    methods: {
        /**
         * Start phone validation for unknown numbers
         */
        validateUnknownPhoneNumbers() {
            if (this.validating_phone_numbers) {
                return;
            }

            this.validating_phone_numbers = true;
            this.phone_validation_status = 'Starting phone validation...';

            this.$rc.rest({
                'spinner': false,
                'endpoint': `PUT: /api/v1/imports/${this.import_type}/validate-endpoints/${this.$route.params.organization_id}/${this.import_id}`,
                'params': {},
                'on_success': (data) => {
                    this.phone_validation_batch_id = data.validation_batch_id;
                    this.phone_validation_status = 'Phone validation in progress...';
                    this.startValidationPolling();
                },
                'on_error': (error) => {
                    this.validating_phone_numbers = false;
                    this.phone_validation_status = '';
                    this.phone_validation_status = 'Failed to start phone validation: ' + error;
                    this.$rc.alert(this.phone_validation_status, 'error');
                }
            });
        },

        /**
         * Allow invalid phone numbers by updating intermediate sheet.
         */
        allowInvalidPhoneNumbers() {
            if (this.validating_phone_numbers) {
                return;
            }
            this.validating_phone_numbers = true;
            this.phone_validation_status = 'Allowing invalid phone numbers...';
            this.$rc.rest({
                'spinner': false,
                'endpoint': `PUT: /api/v1/imports/${this.import_type}/allow-invalid-phone-numbers/${this.$route.params.organization_id}/${this.import_id}`,
                'params': {},
                'on_success': () => {
                    // Regenerate preview to reflect bypassed rows
                    this.regenerateIntermediateSheetAfterValidation();
                },
                'on_error': (error) => {
                    this.validating_phone_numbers = false;
                    this.phone_validation_status = '';
                    this.phone_validation_status = 'Failed to allow invalid phone numbers: ' + error;
                    this.$rc.alert(this.phone_validation_status, 'error');
                }
            });
        },

        /**
         * Start polling for validation status
         */
        startValidationPolling() {
            if (this.phone_validation_polling_interval) {
                clearInterval(this.phone_validation_polling_interval);
            }

            this.phone_validation_polling_interval = setInterval(() => {
                this.pollValidationStatus();
            }, 2000);

            // Initial poll
            this.pollValidationStatus();
        },

        /**
         * Poll validation status
         */
        pollValidationStatus() {
            if (!this.phone_validation_batch_id) {
                this.stopValidationPolling();
                return;
            }

            this.$rc.rest({
                'spinner': false,
                'endpoint': `GET: /api/v1/imports/${this.import_type}/validation-status/${this.$route.params.organization_id}/${this.phone_validation_batch_id}`,
                'params': {},
                'on_success': (data) => {
                    if (data.is_complete) {
                        this.onPhoneValidationComplete();
                    } else if (!data.is_active) {
                        this.onPhoneValidationFailed('Phone validation failed or was cancelled');
                    }
                    // Continue polling if still active and not complete
                },
                'on_error': () => {
                    this.onPhoneValidationFailed('Error checking validation status');
                }
            });
        },

        /**
         * Handle phone validation completion
         */
        onPhoneValidationComplete() {
            this.stopValidationPolling();
            this.phone_validation_status = 'Applying validation results and regenerating preview...';

            // Apply validation results to the import
            this.applyValidationResults();
        },

        /**
         * Handle phone validation failure
         */
        onPhoneValidationFailed(message) {
            this.stopValidationPolling();
            this.validating_phone_numbers = false;
            this.phone_validation_status = '';
            this.$rc.alert('Phone validation failed', message);
        },

        /**
         * Stop validation polling
         */
        stopValidationPolling() {
            if (this.phone_validation_polling_interval) {
                clearInterval(this.phone_validation_polling_interval);
                this.phone_validation_polling_interval = null;
            }
        },

        /**
         * Apply validation results to the import
         */
        applyValidationResults() {
            this.used_phone_validation = true;
            this.regenerateIntermediateSheetAfterValidation();
        },

        /**
         * Regenerate intermediate sheet after phone validation (reuses existing polling)
         */
        regenerateIntermediateSheetAfterValidation() {
            this.phone_validation_status = 'Regenerating preview with validation results...';

            this.$rc.rest({
                'spinner': false,
                'endpoint': `POST: /api/v1/imports/${this.import_type}/create-intermediate/${this.$route.params.organization_id}/${this.import_id}`,
                'params': {},
                'on_success': (data) => {
                    // Reuse existing polling pattern like execution polling
                    this.pollGenericProcess(data.process_id,
                        this.onValidationIntermediateSheetComplete.bind(this),
                        (error) => this.onPhoneValidationFailed(`Failed to regenerate preview: ${error}`)
                    );
                },
                'on_error': () => {
                    this.onPhoneValidationFailed('Failed to regenerate preview after validation');
                }
            });
        },

        /**
         * Handle intermediate sheet generation completion after validation
         */
        onValidationIntermediateSheetComplete() {
            this.validating_phone_numbers = false;
            this.phone_validation_status = 'Phone validation completed successfully!';

            // Reload import metadata and preview data
            this.loadExistingImport();

            // Clear status message after a few seconds
            setTimeout(() => {
                this.phone_validation_status = '';
            }, 3000);
        },
    },

    beforeDestroy() {
        // Clean up polling when component is destroyed
        this.stopValidationPolling();
    }
};
